/** Extract a human-readable message from an axios-style API error response.
 *
 *  Prefers the given DRF field key (e.g. `"team_access"`, whose 400 body is
 *  `{"team_access": "<message>"}` — a bare string, though a `[string]` list is
 *  also tolerated), then the common `detail` / `error` envelopes, and finally
 *  any single field error present in the body.
 *
 *  That last fallback is what makes a cross-field rejection legible: the API
 *  keys such a message to one side of the comparison, which needn't be the field
 *  the user just edited (moving a project's start date past its target
 *  completion is reported under `target_completion_date`). Looking up only the
 *  edited field returns nothing and the caller falls back to a generic "couldn't
 *  save", which tells the user nothing about what to change.
 *
 *  Returns `undefined` when nothing usable is present, so callers can still fall
 *  back to a generic message. */
export function apiFieldErrorMessage(error: unknown, field?: string): string | undefined {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== "object") return undefined;
  const d = data as Record<string, unknown>;
  if (field) {
    const own = fieldMessage(d[field]);
    if (own) return own;
  }
  if (typeof d.detail === "string" && d.detail) return d.detail;
  if (typeof d.error === "string" && d.error) return d.error;
  for (const value of Object.values(d)) {
    const message = fieldMessage(value);
    if (message) return message;
  }
  return undefined;
}

/** A DRF field error is either a bare string or a list of them. */
function fieldMessage(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (Array.isArray(value) && typeof value[0] === "string" && value[0]) return value[0];
  return undefined;
}
